import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Collapse,
  Spin,
  Alert,
  Button,
  Space,
  Typography,
  Descriptions,
  Empty,
} from 'antd';
import {
  ReloadOutlined,
  DesktopOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  WarningOutlined,
  DisconnectOutlined,
  RiseOutlined,
  FallOutlined,
  MinusOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { monitoringApi } from '../../services/monitoring';
import { GpuClusterStats } from '../../types';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

const { Title, Text } = Typography;

const GpuClusterMonitoring: React.FC = () => {
  const [data, setData] = useState<GpuClusterStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { autoRefresh, refreshInterval } = useSelector((state: RootState) => state.settings);

  const refreshData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await monitoringApi.getGpuClusterStats();
      setData(res.data);
    } catch (e: any) {
      setError(e?.message || '获取集群监控数据失败');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useAutoRefresh({
    interval: refreshInterval,
    enabled: autoRefresh,
    onRefresh: refreshData,
  });

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const formatMs = (ms: number) => {
    if (ms == null || ms < 0) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}min`;
  };

  const scaleAdviceTag = (advice: string) => {
    const map: Record<string, { color: string; text: string }> = {
      SCALE_UP: { color: 'orange', text: '建议扩容' },
      SCALE_DOWN: { color: 'blue', text: '可缩容' },
      NORMAL: { color: 'green', text: '正常' },
    };
    const v = map[advice] || { color: 'default', text: advice };
    return <Tag color={v.color}>{v.text}</Tag>;
  };

  const statusTag = (status: string) => {
    const map: Record<string, { color: string; text: string }> = {
      BUSY: { color: 'processing', text: '执行中' },
      IDLE: { color: 'success', text: '空闲' },
      FAULTY: { color: 'error', text: '故障' },
      OFFLINE: { color: 'default', text: '离线' },
    };
    const v = map[status] || { color: 'default', text: status };
    return <Tag color={v.color}>{v.text}</Tag>;
  };

  const flowChangeTag = (rate: number | null) => {
    if (rate == null) return <Text type="secondary">-</Text>;
    const pct = (rate * 100).toFixed(1);
    if (rate > 0) return <Text type="warning"><RiseOutlined /> +{pct}%</Text>;
    if (rate < 0) return <Text type="success"><FallOutlined /> {pct}%</Text>;
    return <Text type="secondary"><MinusOutlined /> 0%</Text>;
  };

  if (loading && !data) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" tip="加载集群监控数据..." />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Alert
          type="error"
          showIcon
          message="加载失败"
          description={error}
          action={
            <Button type="primary" icon={<ReloadOutlined />} onClick={refreshData}>
              重试
            </Button>
          }
        />
      </div>
    );
  }

  if (!data) {
    return <Empty description="暂无集群监控数据" />;
  }

  const { cluster, categories, gpuDetails, timestamp } = data;
  const categoryEntries = Object.entries(categories || {});

  return (
    <div style={{ padding: 0 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <Title level={4} style={{ margin: 0 }}>GPU 集群监控</Title>
        <Space>
          <Text type="secondary">更新于 {timestamp ? new Date(timestamp).toLocaleString() : '-'}</Text>
          <Button icon={<ReloadOutlined />} onClick={refreshData} loading={loading}>刷新</Button>
        </Space>
      </div>

      {/* 1. 概览 */}
      <Card title="集群概览" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={8} md={4}>
            <Statistic title="总 GPU 数" value={cluster?.totalGpus ?? 0} prefix={<DesktopOutlined />} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Statistic title="执行中" value={cluster?.activeGpus ?? 0} valueStyle={{ color: '#1890ff' }} prefix={<PlayCircleOutlined />} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Statistic title="空闲" value={cluster?.idleGpus ?? 0} valueStyle={{ color: '#52c41a' }} prefix={<PauseCircleOutlined />} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Statistic title="故障" value={cluster?.faultyGpus ?? 0} valueStyle={{ color: '#ff4d4f' }} prefix={<WarningOutlined />} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Statistic title="离线" value={cluster?.offlineGpus ?? 0} valueStyle={{ color: '#8c8c8c' }} prefix={<DisconnectOutlined />} />
          </Col>
        </Row>
      </Card>

      {/* 2. 按分类统计 + 任务流量 */}
      <Card title="按分类统计与任务流量" style={{ marginBottom: 16 }}>
        {categoryEntries.length === 0 ? (
          <Empty description="暂无分类数据" />
        ) : (
          <Collapse
            defaultActiveKey={categoryEntries.slice(0, 1).map(([k]) => k)}
            items={categoryEntries.map(([categoryName, stat]) => ({
              key: categoryName,
              label: (
                <Space wrap>
                  <ThunderboltOutlined />
                  <strong>{categoryName}</strong>
                  <Tag color="blue">队列 {stat.queueDepth}</Tag>
                  <Tag>利用率 {(stat.gpuUtilizationRate * 100).toFixed(0)}%</Tag>
                  {scaleAdviceTag(stat.scaleAdvice)}
                  <Text type="secondary">流量 {stat.taskFlowPerMin}/min (5min均: {stat.taskFlowAvg5Min}, 30min均: {stat.taskFlowAvg30Min})</Text>
                </Space>
              ),
              children: (
                <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3 }}>
                  <Descriptions.Item label="总 GPU">{stat.totalGpus}</Descriptions.Item>
                  <Descriptions.Item label="执行中">{stat.activeGpus}</Descriptions.Item>
                  <Descriptions.Item label="空闲">{stat.idleGpus}</Descriptions.Item>
                  <Descriptions.Item label="队列深度">{stat.queueDepth}</Descriptions.Item>
                  <Descriptions.Item label="平均处理耗时">{formatMs(stat.avgProcessingTimeMs)}</Descriptions.Item>
                  <Descriptions.Item label="平均空闲时长">{formatMs(stat.avgIdleDurationMs)}</Descriptions.Item>
                  <Descriptions.Item label="吞吐(任务/分钟)">{stat.throughputPerMin?.toFixed(1) ?? '-'}</Descriptions.Item>
                  <Descriptions.Item label="预计排空时间">{stat.estimatedDrainTimeMin >= 0 ? `${stat.estimatedDrainTimeMin.toFixed(1)} min` : '-'}</Descriptions.Item>
                  <Descriptions.Item label="累计完成">{stat.totalCompletedTasks}</Descriptions.Item>
                  <Descriptions.Item label="上一分钟流量">{stat.taskFlowPerMin}</Descriptions.Item>
                  <Descriptions.Item label="5 分钟均">{stat.taskFlowAvg5Min}</Descriptions.Item>
                  <Descriptions.Item label="30 分钟均">{stat.taskFlowAvg30Min}</Descriptions.Item>
                  <Descriptions.Item label="流量变化率" span={3}>{flowChangeTag(stat.taskFlowChangeRate)}</Descriptions.Item>
                </Descriptions>
              ),
            }))}
          />
        )}
      </Card>

      {/* 3. 任务流量汇总表 */}
      {categoryEntries.length > 0 && (
        <Card title="任务流量一览" style={{ marginBottom: 16 }}>
          <Table
            size="small"
            rowKey="category"
            pagination={false}
            dataSource={categoryEntries.map(([category, stat]) => ({
              category,
              taskFlowPerMin: stat.taskFlowPerMin,
              taskFlowAvg5Min: stat.taskFlowAvg5Min,
              taskFlowAvg30Min: stat.taskFlowAvg30Min,
              taskFlowChangeRate: stat.taskFlowChangeRate,
            }))}
            columns={[
              { title: '分类', dataIndex: 'category', key: 'category', width: 160 },
              { title: '上一分钟', dataIndex: 'taskFlowPerMin', key: 'taskFlowPerMin', width: 100, align: 'right' },
              { title: '5 分钟均', dataIndex: 'taskFlowAvg5Min', key: 'taskFlowAvg5Min', width: 100, align: 'right' },
              { title: '30 分钟均', dataIndex: 'taskFlowAvg30Min', key: 'taskFlowAvg30Min', width: 110, align: 'right' },
              {
                title: '变化率',
                dataIndex: 'taskFlowChangeRate',
                key: 'taskFlowChangeRate',
                width: 120,
                render: (rate: number | null) => flowChangeTag(rate),
              },
            ]}
          />
        </Card>
      )}

      {/* 4. GPU 详情表 */}
      <Card title="GPU 详情">
        {!gpuDetails?.length ? (
          <Empty description="暂无 GPU 详情" />
        ) : (
          <Table
            size="small"
            rowKey="gpuId"
            scroll={{ x: 1200 }}
            dataSource={gpuDetails}
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `共 ${t} 条` }}
            columns={[
              { title: 'GPU ID', dataIndex: 'gpuId', key: 'gpuId', width: 120, fixed: 'left' },
              { title: 'IP', dataIndex: 'ip', key: 'ip', width: 120 },
              { title: '状态', dataIndex: 'effectiveStatus', key: 'effectiveStatus', width: 90, render: (s: string) => statusTag(s) },
              { title: '当前任务', dataIndex: 'currentTaskId', key: 'currentTaskId', width: 140, ellipsis: true, render: (v: string | null) => v || '-' },
              { title: '运行时长', dataIndex: 'taskRunningDurationMs', key: 'taskRunningDurationMs', width: 100, render: (v: number) => formatMs(v ?? 0) },
              { title: '当前空闲时长', dataIndex: 'currentIdleDurationMs', key: 'currentIdleDurationMs', width: 110, render: (v: number) => formatMs(v ?? 0) },
              { title: '上次空闲间隔', dataIndex: 'lastIdleDurationMs', key: 'lastIdleDurationMs', width: 110, render: (v: number) => formatMs(v ?? 0) },
              { title: '累计完成', dataIndex: 'completedTaskCount', key: 'completedTaskCount', width: 90, align: 'right' },
              { title: '平均耗时', dataIndex: 'avgProcessingTimeMs', key: 'avgProcessingTimeMs', width: 100, render: (v: number) => formatMs(v ?? 0) },
              { title: '最后心跳', dataIndex: 'lastHeartbeat', key: 'lastHeartbeat', width: 160, render: (t: number) => t ? new Date(t).toLocaleString() : '-' },
            ]}
          />
        )}
      </Card>
    </div>
  );
};

export default GpuClusterMonitoring;
